import './privacy-policy.scss';

import React, { useEffect } from 'react';
import { Container, Typography, Box, Link, Button } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Translate, translate } from 'react-jhipster';
import { useHistory } from 'react-router-dom';

const PrivacyPolicy = () => {
  const history = useHistory();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBack = () => {
    if (history.length > 1) {
      history.goBack();
    } else {
      history.push('/');
    }
  };

  return (
    <Container maxWidth="md" className="privacy-policy-page" dir="ltr" sx={{ py: 5 }}>
      <Button
        onClick={handleBack}
        startIcon={<ArrowBackIcon />}
        className="privacy-policy-back-btn"
        aria-label={translate('entity.action.back')}
        sx={{ mb: 4 }}
      >
        <Translate contentKey="entity.action.back">Back</Translate>
      </Button>

      <Typography variant="overline" component="p">
        AL-SHMOKH HOLDING CO.
      </Typography>
      <Typography variant="h4" component="h1" gutterBottom>
        Privacy Policy
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Comex Platform Web &amp; Mobile Application &middot; Last updated: August 2026
      </Typography>

      <Typography variant="h6" component="h2" sx={{ mt: 4 }}>
        1. Introduction
      </Typography>
      <Typography paragraph>
        Al-Shmokh Holding Co. (&quot;Al-Shmokh&quot; or &quot;the Company&quot;) operates the Comex platform (the &quot;Comex
        Platform&quot;), including its web application and mobile application (collectively, the &quot;Platform&quot;). This
        Privacy Policy (the &quot;Policy&quot;) explains how the Company collects, uses, discloses, stores, retains,
        processes, and protects personal information in connection with access to and use of the Platform and the
        services provided through it, including Islamic commodity trading services such as Tawarruq and Commodity
        Murabaha.
      </Typography>
      <Typography paragraph>
        For the purposes of this Policy, &quot;Personal Information&quot; means any information relating to an identified
        or identifiable natural person that the Company collects, receives, or otherwise processes in connection with
        the Platform or the Company&apos;s services.
      </Typography>
      <Typography paragraph>
        This Policy is intended to provide transparency regarding the Company&apos;s processing of Personal Information
        and should be read together with any applicable terms and conditions, account agreements, financing or
        investment documentation, and other notices provided by the Company.
      </Typography>
      <Typography paragraph>
        By registering for, accessing, or using the Platform, the relevant individual acknowledges having had the
        opportunity to review this Policy. Registration for access to, or use of the Platform does not, by itself,
        constitute consent to processing for which consent is legally required. Where consent is required under
        applicable laws and regulations, the Company will obtain and record such consent in the manner required by
        applicable laws and regulations.
      </Typography>
      <Typography paragraph>
        The Company processes Personal Information only where permitted by applicable laws and regulations and for
        specified and legitimate purposes, including providing and administering the Platform and the Company&apos;s
        services, fulfilling contractual and legal obligations, complying with regulatory requirements, protecting
        our rights and interests, maintaining the security of the Platform, and other purposes disclosed to you or
        otherwise permitted by applicable laws and regulations.
      </Typography>

      <Typography variant="h6" component="h2" sx={{ mt: 4 }}>
        2. Information We Collect
      </Typography>
      <Typography paragraph>
        Depending on how the Platform is used and the services requested, the Company may collect the following
        categories of Personal Information.
      </Typography>

      <Typography variant="subtitle1" component="h3" sx={{ mt: 2 }}>
        2.1 Personal Identification Information
      </Typography>
      <ul>
        <li>Full legal name, as it appears on your government-issued identification document;</li>
        <li>National identification number / Civil ID number;</li>
        <li>Date of birth;</li>
        <li>Email address;</li>
        <li>Mobile phone number;</li>
        <li>Residential address; and</li>
        <li>Other identification or verification information reasonably required to establish or verify an individual&apos;s identity.</li>
      </ul>

      <Typography variant="subtitle1" component="h3" sx={{ mt: 2 }}>
        2.2 Financial Information
      </Typography>
      <ul>
        <li>Bank account details, including IBAN;</li>
        <li>Financing amount and applicable repayment terms;</li>
        <li>Investment amount and applicable profit terms;</li>
        <li>Transaction history, including quotations, purchase orders and certificates; and</li>
        <li>Commodity trading records, including BID, OTC and STB certificates.</li>
      </ul>

      <Typography variant="subtitle1" component="h3" sx={{ mt: 2 }}>
        2.3 Account and Authentication Information
      </Typography>
      <ul>
        <li>Username and password credentials;</li>
        <li>One-time passwords (OTP) used for authentication;</li>
        <li>Session tokens and related authentication information; and</li>
        <li>Information necessary to maintain the security and integrity of your account.</li>
      </ul>

      <Typography variant="subtitle1" component="h3" sx={{ mt: 2 }}>
        2.4 Usage and Technical Data
      </Typography>
      <ul>
        <li>Login history and timestamps;</li>
        <li>Device type and operating system, when the Platform is accessed through the mobile application;</li>
        <li>IP address;</li>
        <li>Pages, functions, and features accessed within the Platform;</li>
        <li>Browser and technical configuration information, where applicable; and</li>
        <li>Error logs, diagnostic information and crash reports.</li>
      </ul>
      <Typography paragraph>
        The Company may use usage and technical information to maintain the security, functionality, availability,
        and performance of the Platform and to identify, investigate, prevent, and address technical issues,
        unauthorized access, fraud, misuse, and other security incidents.
      </Typography>

      <Typography variant="subtitle1" component="h3" sx={{ mt: 2 }}>
        2.5 Documents and Other Information Provided
      </Typography>
      <Typography paragraph>The Company may collect documents or other information submitted through the Platform, including:</Typography>
      <ul>
        <li>Identity documents submitted for verification;</li>
        <li>Signed agreements and authorization forms, including powers of attorney;</li>
        <li>Information submitted in connection with financing, investment, commodity trading, or other requested services; and</li>
        <li>Other documents or information provided where reasonably necessary to provide the Company&apos;s services, comply with applicable laws and regulations, verify an individual&apos;s identity, or respond to requests.</li>
      </ul>

      <Typography variant="h6" component="h2" sx={{ mt: 4 }}>
        3. How We Use Your Information
      </Typography>
      <Typography paragraph>The Company may process Personal Information for the following purposes:</Typography>
      <ul>
        <li><strong>Transaction Processing:</strong> To execute and administer Islamic commodity trading transactions, including Tawarruq and Commodity Murabaha, on your behalf, including generating BID, OTC, and STB certificates and processing purchase orders.</li>
        <li><strong>Account Management:</strong> To create and manage accounts, verify identity, authenticate access to the Platform, administer the Company&apos;s relationship with account holders, and provide requested services.</li>
        <li><strong>Regulatory, Legal and Shariah Compliance:</strong> To comply with applicable laws and regulations in the State of Kuwait, including applicable requirements of the Central Bank of Kuwait in relation to regulated Islamic financing activities, applicable Shariah principles and requirements, anti-money laundering and counter-terrorist financing requirements, Know Your Customer (KYC) obligations, sanctions requirements, financial and regulatory requirements, court orders, and other requirements of competent regulatory or supervisory authorities.</li>
        <li><strong>Communications:</strong> To send transaction confirmations, notifications, statements, service-related communications, security alerts, and important service updates via email or SMS, or other available communication channels.</li>
        <li><strong>Customer Support:</strong> To respond to enquiries, provide technical assistance, resolve complaints and disputes, and otherwise support access to and use of the Platform.</li>
        <li><strong>Security and Fraud Prevention:</strong> To detect, prevent, investigate, and address fraud, unauthorized access, misuse of the Platform, security incidents, and other prohibited or unlawful activities.</li>
        <li><strong>Service Improvement:</strong> To analyze usage and operational information and improve the features, functionality, security, reliability, and performance of the Platform.</li>
        <li><strong>Legal, Regulatory and Reporting Obligations:</strong> To comply with legal, regulatory, judicial, supervisory, reporting, and record-keeping requirements and to respond to lawful requests from competent authorities.</li>
        <li><strong>Protection of Rights:</strong> To establish, exercise, or defend our legal rights and interests, including in connection with disputes, claims, investigations, audits, proceedings, or enforcement of agreements.</li>
      </ul>
      <Typography paragraph>
        The Company will not process Personal Information for purposes that are incompatible with the purposes
        described in this Policy unless otherwise permitted or required by applicable laws and regulations or
        applicable Shariah principles and requirements or, where required, with the relevant individual&apos;s consent.
      </Typography>

      <Typography variant="h6" component="h2" sx={{ mt: 4 }}>
        4. Sharing and Disclosure of Personal Information
      </Typography>
      <Typography variant="subtitle1" component="h3" sx={{ mt: 2 }}>
        4.1 Third Party Sharing and Disclosure
      </Typography>
      <Typography paragraph>
        The Company does not sell Personal Information to third parties. The Company may, however, disclose or
        otherwise make Personal Information available to third parties where reasonably necessary to provide the
        Company&apos;s services or where required or permitted by applicable laws and regulations.
      </Typography>
      <Typography paragraph>Such third parties may include:</Typography>
      <ul>
        <li><strong>Partner Banks and Financial Institutions:</strong> Information may be shared with banks, branches, financing providers, or other financial institutions involved in facilitating a transaction, financing, investment, settlement, or related service.</li>
        <li><strong>Bursa Malaysia Islamic Services:</strong> Transaction details are submitted to Bursa Malaysia&apos;s commodity trading platform to generate official certificates required for Shariah-compliant transactions.</li>
        <li><strong>Cloud Infrastructure Providers:</strong> The Company uses Amazon Web Services (AWS) for secure data storage, document hosting, and email delivery. AWS processes data under strict confidentiality and data processing agreements.</li>
        <li><strong>Regulatory and Government Authorities:</strong> The Company may disclose Personal Information to government bodies, financial regulators, or law enforcement authorities where required or permitted by applicable laws and regulations, court order, or other lawful process.</li>
        <li><strong>Service Providers:</strong> Trusted third-party vendors who assist in operating the Platform are bound by confidentiality and data processing agreements.</li>
        <li><strong>Corporate Transactions:</strong> Where permitted by applicable laws and regulations, Personal Information may be disclosed in connection with a merger, acquisition, restructuring, financing, sale of assets, or other corporate transaction, subject to appropriate confidentiality and data-protection measures.</li>
      </ul>
      <Typography paragraph>
        All third parties who receive Personal Information are required to handle it in accordance with applicable
        data protection laws and the Company&apos;s confidentiality requirements.
      </Typography>

      <Typography variant="subtitle1" component="h3" sx={{ mt: 2 }}>
        4.2 International Transfers
      </Typography>
      <Typography paragraph>
        Personal Information may be processed or stored outside the State of Kuwait where reasonably necessary for
        the provision, operation, security, or support of the Platform or our services, including where third-party
        service providers or cloud infrastructure providers are located outside Kuwait.
      </Typography>
      <Typography paragraph>
        Where Personal Information is transferred outside Kuwait, the Company will take reasonable steps to ensure
        that the transfer and subsequent processing comply with applicable laws and regulations and any applicable
        requirements relating to international or cross-border transfers, including any required safeguards,
        approvals, notifications, or contractual protections.
      </Typography>

      <Typography variant="h6" component="h2" sx={{ mt: 4 }}>
        5. Data Retention
      </Typography>
      <Typography paragraph>
        The Company retains Personal Information only for as long as reasonably necessary for the purposes for which
        it was collected, including to provide our services, maintain business and transaction records, comply with
        legal and regulatory obligations, resolve disputes, enforce agreements, prevent fraud, and protect our legal
        rights and interests.
      </Typography>
      <Typography paragraph>
        Certain information may be retained for longer periods where required by applicable laws and regulations or
        where necessary for the establishment, exercise, or defense of legal claims.
      </Typography>
      <Typography paragraph>
        Financial transaction records are retained for a minimum of seven (7) years in compliance with applicable
        financial record-keeping requirements.
      </Typography>
      <Typography paragraph>
        The Company determines applicable retention periods having regard to the nature and purpose of the
        information, applicable legal and regulatory requirements, and legitimate business and record-keeping needs.
      </Typography>
      <Typography paragraph>
        An individual may request deletion of their account or Personal Information, subject to applicable laws and
        any legal, regulatory, contractual, or legitimate business requirement that requires us to retain particular
        information.
      </Typography>
      <Typography paragraph>
        Where Personal Information is no longer required and there is no legal, regulatory, contractual, or
        legitimate business requirement to retain it, we will take reasonable steps to securely delete, anonymize,
        or otherwise dispose of it in accordance with our retention and security procedures.
      </Typography>

      <Typography variant="h6" component="h2" sx={{ mt: 4 }}>
        6. Data Security
      </Typography>
      <Typography paragraph>
        The Company implements appropriate technical and organizational measures designed to protect Personal
        Information against unauthorized access, disclosure, alteration, loss, destruction, misuse, and other
        unlawful or unauthorized processing, taking into account the nature of the information and the risks
        associated with its processing.
      </Typography>
      <Typography paragraph>Depending on the circumstances, these measures may include:</Typography>
      <ul>
        <li>Encryption of data in transit using TLS (HTTPS);</li>
        <li>Encrypted or equivalent protection of sensitive data at rest;</li>
        <li>Role-based access controls limiting access to authorized personnel;</li>
        <li>Token-based authentication, including JWT where applicable, with appropriate expiry controls;</li>
        <li>One-time password (OTP) verification for sensitive operations;</li>
        <li>Regular security reviews and access audits; and</li>
        <li>Incident response and business continuity procedures, as appropriate.</li>
      </ul>
      <Typography paragraph>
        No method of electronic transmission, storage, or processing can be guaranteed to be completely secure.
        Accordingly, while the Company takes reasonable measures to protect Personal Information, it cannot
        guarantee absolute security.
      </Typography>
      <Typography paragraph>
        In the event of a Personal Data breach or security incident, the Company will take reasonable steps to
        investigate, contain, mitigate, and remediate the incident and comply with any notification, reporting, or
        other obligations imposed by applicable laws and regulations.
      </Typography>

      <Typography variant="h6" component="h2" sx={{ mt: 4 }}>
        7. Your Rights
      </Typography>
      <Typography paragraph>
        Subject to applicable laws and any applicable exemptions or restrictions, you have rights in relation to
        your Personal Information, which may include:
      </Typography>
      <ul>
        <li><strong>Access:</strong> Request information regarding, or access to, Personal Information held about you.</li>
        <li><strong>Correction:</strong> Request correction or updating of inaccurate or incomplete Personal Information.</li>
        <li><strong>Deletion:</strong> Request deletion of Personal Information where permitted by applicable laws and regulations and where there is no legal or other lawful basis requiring continued retention.</li>
        <li><strong>Objection:</strong> Object to processing of Personal Information in certain circumstances, where such right is available under applicable laws and regulations.</li>
        <li><strong>Data Portability:</strong> Request a copy of your data in a structured, commonly used, and machine-readable format, where such right is available under applicable laws and regulations.</li>
        <li><strong>Withdrawal of Consent:</strong> Where processing is based on your consent, withdraw that consent at any time, without affecting the lawfulness of processing carried out before withdrawal.</li>
        <li><strong>Other Statutory Rights:</strong> Exercise any other rights available to you under applicable laws and regulations.</li>
      </ul>
      <Typography paragraph>
        To exercise any of these rights, please contact the Company using the contact details in Section 12. The
        Company may request information reasonably necessary to verify your identity and the authenticity of a
        request before processing it.
      </Typography>
      <Typography paragraph>
        The Company may refuse, limit, or defer a request where permitted or required by applicable laws and
        regulations, including where fulfilling the request would conflict with legal, regulatory, judicial,
        contractual, security, fraud-prevention, or record-retention obligations.
      </Typography>

      <Typography variant="h6" component="h2" sx={{ mt: 4 }}>
        8. Cookies and Local Storage
      </Typography>
      <Typography paragraph>
        The web application may use session cookies, local storage, and similar technologies that are necessary to
        maintain authenticated sessions, protect account security, and operate the Platform. These technologies may
        be essential to secure operations of the Platform and may not be capable of being disabled while using
        certain Platform functions.
      </Typography>
      <Typography paragraph>
        The Company does not use third-party advertising cookies or behavioral tracking technologies through the
        Platform.
      </Typography>

      <Typography variant="h6" component="h2" sx={{ mt: 4 }}>
        9. Children&apos;s Privacy
      </Typography>
      <Typography paragraph>
        The Platform is intended solely for adults aged 21 years and above who are legally eligible to enter into
        the relevant financial or commercial arrangements.
      </Typography>
      <Typography paragraph>
        The Company does not knowingly collect Personal Information from individuals under 21 through the Platform.
        If an individual believes that a minor has submitted Personal Information through the Platform, please
        contact the Company immediately using the contact details in Section 12.
      </Typography>
      <Typography paragraph>
        Where the Company becomes aware that Personal Information has been collected from an individual under 21
        contrary to applicable laws, we will take reasonable steps to address and remove the information, subject to
        any legal or regulatory obligations requiring its retention.
      </Typography>

      <Typography variant="h6" component="h2" sx={{ mt: 4 }}>
        10. Changes to This Policy
      </Typography>
      <Typography paragraph>
        The Company may update this Privacy Policy from time to time to reflect changes in our services, processing
        activities, technology, applicable laws and regulations, or regulatory requirements.
      </Typography>
      <Typography paragraph>
        The Company will notify users of material changes by publishing the updated policy on the Platform and
        revising the &quot;Last updated&quot; date.
      </Typography>
      <Typography paragraph>
        Where required by applicable laws, the Company will provide additional notice or obtain consent in
        connection with material changes to the Policy.
      </Typography>

      <Typography variant="h6" component="h2" sx={{ mt: 4 }}>
        11. Governing Law
      </Typography>
      <Typography paragraph>
        This Policy shall be governed by and construed in accordance with the laws of the State of Kuwait.
      </Typography>
      <Typography paragraph>
        Any dispute arising out of or in connection with this Policy shall be subject to the jurisdiction of the
        competent courts of the State of Kuwait.
      </Typography>

      <Typography variant="h6" component="h2" sx={{ mt: 4 }}>
        12. Contact Us
      </Typography>
      <Typography paragraph>
        For any questions, concerns, or requests relating to this Privacy Policy or the Company&apos;s processing of
        Personal Information, please contact the Company using the contact details set below.
      </Typography>
      <Typography paragraph>
        The Company will notify users of any changes to its contact details by updating the contact information
        provided on the Platform.
      </Typography>
      <Box className="privacy-policy-contact-box">
        <Typography variant="subtitle1" component="p" sx={{ fontWeight: 'bold' }}>
          Al-Shmokh Holding Co.
        </Typography>
        <Typography component="p">
          Email: <Link href="mailto:legal@sh-global.com">legal@sh-global.com</Link>
        </Typography>
        <Typography component="p">
          URL:{' '}
          <Link href="https://sh-trade.net/" target="_blank" rel="noopener noreferrer">
            https://sh-trade.net/
          </Link>
        </Typography>
      </Box>

      <Box className="privacy-policy-draft-banner" sx={{ mt: 4 }}>
        <Translate contentKey="comexApp.privacyPolicy.draftNotice">
          This is a draft privacy policy pending legal review and confirmation of governing law, regulatory references,
          retention periods, and contact details. It is not yet final.
        </Translate>
      </Box>
    </Container>
  );
};

export default PrivacyPolicy;
